import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { LoginInfoService } from './login-info.service';
import { LoginInfo as LoginInfoType, GetLoginInfosRequest } from './login-info.type';
import { AuthService } from '../auth/auth.service';
import { Header } from '../shared/header/header';
import { LoginInfoFormDialog } from './login-info-form-dialog/login-info-form-dialog';
import { RelatedStocksDialog } from './related-stocks-dialog/related-stocks-dialog';
import { ConfirmDialog } from '../shared/confirm-dialog/confirm-dialog';
import { ImportToStocksDialog } from './import-to-stocks-dialog/import-to-stocks-dialog';

@Component({
  selector: 'app-login-info',
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatMenuModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatPaginatorModule,
    MatCheckboxModule,
    Header,
  ],
  templateUrl: './login-info.html',
  styleUrl: './login-info.scss'
})
export class LoginInfo implements OnInit {
  private loginInfoService = inject(LoginInfoService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private authService = inject(AuthService);

  displayedColumns: string[] = ['select', 'email', 'created_at', 'action'];
  loginInfos: LoginInfoType[] = [];
  loading: boolean = true;

  // Pagination
  pageIndex: number = 0;
  pageSize: number = 20;
  totalRecords: number = 0;
  totalPages: number = 0;

  // Search
  searchQuery: string = '';

  // Selection
  selectedEmails: Set<string> = new Set();
  selectAll: boolean = false;

  ngOnInit(): void {
    this.loadLoginInfos();
  }

  loadLoginInfos(): void {
    this.loading = true;

    const request: GetLoginInfosRequest = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
    };

    if (this.searchQuery && this.searchQuery.trim()) {
      request.search = this.searchQuery.trim();
    }

    this.loginInfoService.getLoginInfos(request).subscribe({
      next: (response) => {
        this.loginInfos = response.login_infos ?? [];
        this.totalRecords = response.total;
        this.totalPages = response.total_pages;
        this.loading = false;
      },
      error: (error) => {
        console.error('Failed to load login infos:', error);
        this.snackBar.open('Error loading login info records', 'Close', { duration: 5000 });
        this.loading = false;
      }
    });
  }

  onSearchChange(): void {
    this.pageIndex = 0;
    this.loadLoginInfos();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.pageIndex = 0;
    this.loadLoginInfos();
  }

  onPage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadLoginInfos();
  }

  showRelatedStocks(loginInfo: LoginInfoType): void {
    this.dialog.open(RelatedStocksDialog, {
      width: '90vw',
      maxWidth: '1200px',
      maxHeight: '90vh',
      data: { email: loginInfo.email }
    });
  }

  openCreateDialog(): void {
    const isMobile = window.innerWidth <= 768;
    const dialogRef = this.dialog.open(LoginInfoFormDialog, {
      width: isMobile ? '100vw' : '600px',
      maxWidth: isMobile ? '100vw' : '90vw',
      height: isMobile ? '100vh' : undefined,
      maxHeight: isMobile ? '100vh' : '90vh',
      disableClose: true,
      data: { mode: 'create' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadLoginInfos();
      }
    });
  }

  openEditDialog(loginInfo: LoginInfoType): void {
    const isMobile = window.innerWidth <= 768;
    const dialogRef = this.dialog.open(LoginInfoFormDialog, {
      width: isMobile ? '100vw' : '600px',
      maxWidth: isMobile ? '100vw' : '90vw',
      height: isMobile ? '100vh' : undefined,
      maxHeight: isMobile ? '100vh' : '90vh',
      disableClose: true,
      data: { mode: 'edit', loginInfo }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadLoginInfos();
      }
    });
  }

  deleteLoginInfo(loginInfo: LoginInfoType): void {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      maxWidth: 'unset',
      data: {
        title: 'Delete Login Info',
        message: `Are you sure you want to delete login info for ${loginInfo.email}? This action cannot be undone.`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loginInfoService.deleteLoginInfo(loginInfo.email).subscribe({
          next: () => {
            this.snackBar.open('Login info deleted successfully', 'Close', { duration: 5000 });
            this.loadLoginInfos();
          },
          error: (error) => {
            console.error('Failed to delete login info:', error);
            const errorMessage = error.error?.error || 'Error deleting login info';
            this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
          }
        });
      }
    });
  }

  refreshList(): void {
    this.loadLoginInfos();
  }

  // Selection methods
  toggleSelectAll(): void {
    if (this.selectAll) {
      this.loginInfos.forEach(info => this.selectedEmails.add(info.email));
    } else {
      this.selectedEmails.clear();
    }
  }

  toggleSelection(email: string): void {
    if (this.selectedEmails.has(email)) {
      this.selectedEmails.delete(email);
    } else {
      this.selectedEmails.add(email);
    }
    this.updateSelectAllState();
  }

  isSelected(email: string): boolean {
    return this.selectedEmails.has(email);
  }

  updateSelectAllState(): void {
    this.selectAll = this.loginInfos.length > 0 &&
                     this.loginInfos.every(info => this.selectedEmails.has(info.email));
  }

  clearSelection(): void {
    this.selectedEmails.clear();
    this.selectAll = false;
  }

  get selectedCount(): number {
    return this.selectedEmails.size;
  }

  get importButtonText(): string {
    if (this.selectedCount === 0) {
      return 'Add accounts (select some first)';
    }
    return `Add accounts (${this.selectedCount} selected)`;
  }

  // Import methods
  openImportDialog(): void {
    // "All" is gone: there is no server-side bulk import any more, and one
    // persistent stock per account means this issues one request per email.
    // Requiring an explicit selection keeps that bounded and visible.
    if (this.selectedCount === 0) {
      this.snackBar.open('Select the accounts to add first.', 'Close', { duration: 4000 });
      return;
    }
    const emails = Array.from(this.selectedEmails);

    const isMobile = window.innerWidth <= 768;
    const dialogRef = this.dialog.open(ImportToStocksDialog, {
      width: isMobile ? '100vw' : '700px',
      maxWidth: isMobile ? '100vw' : '90vw',
      height: isMobile ? '100vh' : undefined,
      maxHeight: isMobile ? '100vh' : '90vh',
      disableClose: true,
      data: { emails }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.clearSelection();
        this.loadLoginInfos();
      }
    });
  }

  importSingleToStocks(loginInfo: LoginInfoType): void {
    const isMobile = window.innerWidth <= 768;
    const dialogRef = this.dialog.open(ImportToStocksDialog, {
      width: isMobile ? '100vw' : '700px',
      maxWidth: isMobile ? '100vw' : '90vw',
      height: isMobile ? '100vh' : undefined,
      maxHeight: isMobile ? '100vh' : '90vh',
      disableClose: true,
      data: { emails: [loginInfo.email] }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadLoginInfos();
      }
    });
  }

  get canEdit(): boolean {
    return this.authService.canWrite('accounts_reports');
  }

  get canAddToStocks(): boolean {
    return this.authService.canWrite('contracts');
  }

  get canViewStocks(): boolean {
    return this.authService.canRead('contracts');
  }

  get isSuperAdmin(): boolean {
    return this.authService.hasRole('SuperAdmin');
  }

  get isAdmin(): boolean {
    return this.authService.hasAnyRole('Admin', 'SuperAdmin');
  }
}
